import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Leaf, Mail, Lock, Loader2, Eye, EyeOff, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { useSubscription } from '@/contexts/SubscriptionContext';

const LoginPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refreshSubscription } = useSubscription();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  // Check if redirected due to expired session
  useEffect(() => {
    if (searchParams.get('expired') === 'true') {
      setSessionExpired(true);
      toast.info(
        i18n.language === 'de' 
          ? 'Deine Sitzung ist abgelaufen. Bitte melde dich erneut an.' 
          : 'Your session has expired. Please log in again.'
      );
    }
  }, [searchParams, i18n.language]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok) {
        // Store the real JWT in localStorage so all ~150 fetch sites that send
        // `Authorization: Bearer <token>` work on any CORS configuration.
        // The backend also sets an httpOnly cookie as a bonus defense layer.
        localStorage.setItem('token', data.access_token);
        localStorage.setItem('user', JSON.stringify(data.user));
        localStorage.setItem('userEmail', data.user.email);

        toast.success(
          i18n.language === 'de' 
            ? `Willkommen zurück, ${data.user.full_name || data.user.email}!` 
            : `Welcome back, ${data.user.full_name || data.user.email}!`
        );

        // Refresh subscription context immediately
        await refreshSubscription();

        // Redirect to home or previous page
        const from = sessionStorage.getItem('loginRedirect') || '/';
        sessionStorage.removeItem('loginRedirect');
        
        setTimeout(() => {
          navigate(from);
        }, 500);
      } else {
        toast.error(
          i18n.language === 'de' 
            ? data.detail || 'Anmeldung fehlgeschlagen. Bitte überprüfe deine Daten.' 
            : data.detail || 'Login failed. Please check your credentials.'
        );
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error(
        i18n.language === 'de' 
          ? 'Verbindungsfehler. Bitte versuche es später erneut.' 
          : 'Connection error. Please try again later.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5 flex items-center justify-center p-4">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md relative z-10"
      >
        {/* Logo */}
        <Link to="/" className="flex items-center justify-center gap-3 mb-8">
          <img 
            src="/logo.png" 
            alt="AsceticLife Logo" 
            className="h-12 w-12 object-contain rounded-lg"
          />
          <span className="text-2xl font-serif font-semibold text-foreground">
            AsceticLife
          </span>
        </Link>

        <Card className="shadow-elevated border-2">
          <CardHeader className="text-center space-y-2">
            <CardTitle className="text-3xl font-serif">
              {i18n.language === 'de' ? 'Anmelden' : 'Sign In'}
            </CardTitle>
            <CardDescription>
              {i18n.language === 'de' 
                ? 'Willkommen zurück! Melde dich mit deinem Konto an.' 
                : 'Welcome back! Sign in to your account.'}
            </CardDescription>
          </CardHeader>

          <CardContent>
            {/* Session Expired Alert */}
            {sessionExpired && (
              <Alert variant="warning" className="mb-4 bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-800">
                <AlertCircle className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-amber-800 dark:text-amber-200">
                  {i18n.language === 'de' 
                    ? 'Deine Sitzung ist abgelaufen. Bitte melde dich erneut an.' 
                    : 'Your session has expired. Please log in again.'}
                </AlertDescription>
              </Alert>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Email Field */}
              <div className="space-y-2">
                <Label htmlFor="email">
                  {i18n.language === 'de' ? 'E-Mail' : 'Email'}
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder={i18n.language === 'de' ? 'deine@email.de' : 'your@email.com'}
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="pl-10"
                    disabled={loading}
                  />
                </div>
              </div>

              {/* Password Field */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">
                    {i18n.language === 'de' ? 'Passwort' : 'Password'}
                  </Label>
                  <Link 
                    to="/forgot-password" 
                    className="text-sm text-[#006039] dark:text-emerald-400 hover:underline"
                    data-testid="forgot-password-link"
                  >
                    {i18n.language === 'de' ? 'Passwort vergessen?' : 'Forgot password?'}
                  </Link>
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    className="pl-10 pr-10"
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-[#00915a] via-[#006039] to-[#004d2e] hover:from-[#00a066] hover:via-[#006d40] hover:to-[#005a36] text-white shadow-lg hover:shadow-xl transition-all"
                size="lg"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {i18n.language === 'de' ? 'Anmelden...' : 'Signing in...'}
                  </>
                ) : (
                  i18n.language === 'de' ? 'Anmelden' : 'Sign In'
                )}
              </Button>
            </form>

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-slate-200 dark:border-slate-700" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-card px-2 text-muted-foreground">
                  {i18n.language === 'de' ? 'Oder' : 'Or'}
                </span>
              </div>
            </div>

            {/* Footer Links */}
            <div className="text-center space-y-2">
              <p className="text-sm text-muted-foreground">
                {i18n.language === 'de' ? 'Noch kein Konto?' : "Don't have an account?"}{' '}
                <Link to="/register" className="text-[#006039] dark:text-emerald-400 hover:underline font-medium">
                  {i18n.language === 'de' ? 'Registrieren' : 'Sign up'}
                </Link>
              </p>
              <Link to="/" className="text-sm text-muted-foreground hover:text-[#006039] dark:hover:text-emerald-400 transition-colors block">
                {i18n.language === 'de' ? '← Zurück zur Startseite' : '← Back to home'}
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Additional Info */}
        <p className="text-center text-sm text-muted-foreground mt-6">
          {i18n.language === 'de' 
            ? 'Durch die Anmeldung stimmst du unseren Nutzungsbedingungen zu.' 
            : 'By signing in, you agree to our Terms of Service.'}
        </p>
      </motion.div>
    </div>
  );
};

export default LoginPage;